"use client";

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
const navItems = [{
  label: "Services",
  href: "/#our-inspection-services"
}, {
  label: "Areas Served",
  href: "/#service-areas"
}, {
  label: "About",
  href: "/#the-trusted-inspections-advantage"
}, {
  label: "Contact",
  href: "/#schedule-your-inspection"
}];
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2.5">
              <ShieldCheck className="size-6 text-primary shrink-0" />
              <span className="text-base sm:text-lg font-bold uppercase tracking-tight text-foreground font-sans">
                Trusted Inspections
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-6">
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item, i) => <Link key={item.label} to={item.href} data-index={i} className="text-sm font-bold uppercase text-foreground transition-colors duration-[40ms] hover:text-primary">
                  {item.label}
                </Link>)}
              <Button asChild className="bg-primary text-primary-foreground font-bold uppercase tracking-wide shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg">
                {pathname === "/" ? <Link to="#schedule-your-inspection">
                    Schedule
                  </Link> : <Link to="#">
                    Schedule
                  </Link>}
              </Button>
            </nav>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary" aria-label="Toggle menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader className="text-left">
                  <SheetTitle className="text-primary font-bold uppercase tracking-tight">Navigation</SheetTitle>
                  <SheetDescription className="sr-only">
                    Mobile navigation menu for Trusted Inspections
                  </SheetDescription>
                </SheetHeader>
                <nav className="flex flex-col py-6 gap-4">
                  {navItems.map((item, i) => <Link key={item.label} to={item.href} data-index={i} className="block py-2 text-base font-bold uppercase text-foreground transition-colors hover:text-primary">
                      {item.label}
                    </Link>)}
                  <div className="pt-4 border-t border-border">
                    <Button asChild className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wide shadow-md">
                      {pathname === "/" ? <Link to="#schedule-your-inspection">
                          Schedule
                        </Link> : <Link to="#">
                          Schedule
                        </Link>}
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}